// src/portalSession.ts
//
// SuitPilot platform single sign-on.
//
// The portal issues its access token as a cookie on `.suitpilot.com` and does
// NOT mark it httpOnly (the portal's own frontend reads it from document.cookie
// for the Bearer header). iredact.suitpilot.com is inside that domain, so a user
// who has signed into the portal - by password or Microsoft SSO - already has a
// usable credential here.
//
// The backend accepts these portal RS256 tokens alongside iRedact's own HS256
// ones (see backend/platform_identity.py), so adoption is purely a
// frontend concern: pick up the cookie, ask the API who it belongs to, and
// populate the same auth state a normal login would.
//
// Role and org are deliberately taken from GET /api/auth/me rather than decoded
// from the token: the portal token carries *portal* role and tenant, while
// iRedact needs its own role and organization, which only the backend can
// resolve (via users.portal_user_id).

import { isTokenExpired } from "./auth";
import { getApiBase } from "./config";

const PORTAL_COOKIE = "sp_access_token";

// Set when the user explicitly logs out of iRedact. Without it, logout would be
// undone on the next render by re-adopting the still-valid portal cookie,
// leaving the user unable to sign out. Session-scoped so closing the tab
// restores normal SSO behaviour.
const SUPPRESS_KEY = "suppress_portal_session";

export function readPortalCookie(): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + PORTAL_COOKIE + "=([^;]*)"),
  );
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function suppressPortalSession() {
  sessionStorage.setItem(SUPPRESS_KEY, "1");
}

export function allowPortalSession() {
  sessionStorage.removeItem(SUPPRESS_KEY);
}

function isSuppressed(): boolean {
  return sessionStorage.getItem(SUPPRESS_KEY) === "1";
}

export type AdoptedSession = {
  token: string;
  role: string;
  orgId: string | null;
};

/**
 * Adopt a portal session if one is available and no valid local session exists.
 * Returns null when there is nothing to adopt - the caller then shows the
 * normal iRedact login form.
 */
export async function adoptPortalSession(): Promise<AdoptedSession | null> {
  if (isSuppressed()) return null;

  const existing = localStorage.getItem("access_token");
  if (existing && !isTokenExpired(existing)) return null; // already signed in

  const token = readPortalCookie();
  if (!token || isTokenExpired(token)) return null;

  // Ask the API who this is. A 403 here is meaningful and expected: a portal
  // account with no linked iRedact user, or a tenant without an active iRedact
  // subscription. In that case fall through to the normal login form rather
  // than trapping the user on an error screen.
  let res: Response;
  try {
    res = await fetch(`${getApiBase()}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;

  const me = await res.json().catch(() => null);
  if (!me?.role) return null;

  return {
    token,
    role: String(me.role).toUpperCase(),
    orgId: me.org_id ? String(me.org_id) : null,
  };
}
